/*
Copyright (C) 2020  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { Info, ListChecks, LoaderCircle, Monitor, Power, Trash2 } from "lucide-react";

import type { Endpoint } from "@src/@types/Endpoint";
import type { Field } from "@src/@types/Field";
import { TransferItem, DeploymentItem } from "@src/@types/MainItem";
import { MinionPoolDetails } from "@src/@types/MinionPool";
import {
  ActionButton,
  ActionGroup,
  DetailsActions,
  DetailsPage,
  DetailsTabs,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import { T9 } from "@src/components/ui/Lists/ListTable";
import MinionPoolMachines from "./MinionPoolMachines";
import MinionPoolEvents from "./MinionPoolEvents";
import MinionPoolMainDetails from "./MinionPoolMainDetails";

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 160px;
  color: ${T9.slate500};
  font-size: 14px;
  svg {
    color: ${T9.slate400};
    animation: mpd-spin 1s linear infinite;
  }
  @keyframes mpd-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const NavigationItems = [
  {
    label: "Overview",
    value: "",
    icon: Info,
  },
  {
    label: "Agent machines",
    value: "machines",
    icon: Monitor,
  },
  {
    label: "Events",
    value: "events",
    icon: ListChecks,
  },
];

type Props = {
  item?: MinionPoolDetails | null;
  itemId: string;
  transfers: TransferItem[];
  deployments: DeploymentItem[];
  endpoints: Endpoint[];
  schema: Field[];
  schemaLoading: boolean;
  loading: boolean;
  page: string;
  onAllocate: () => void;
  onDeleteMinionPoolClick: () => void;
};
@observer
class MinionPoolDetailsContent extends React.Component<Props> {
  isEndpointMissing() {
    const endpoint = this.props.endpoints.find(
      e => e.id === this.props.item?.endpoint_id,
    );

    return Boolean(!endpoint);
  }

  renderBottomControls() {
    const status = this.props.item?.status;
    const deleteEnabled = status === "DEALLOCATED" || status === "ERROR";
    const deallocated = this.props.item?.status === "DEALLOCATED";

    return (
      <DetailsActions>
        <ActionGroup>
          <ActionButton
            disabled={this.isEndpointMissing() || !deallocated}
            title={!deallocated ? "The SwiftIQ Agent must be deallocated first" : undefined}
            onClick={() => {
              this.props.onAllocate();
            }}
          >
            <Power size={16} />
            Allocate
          </ActionButton>
        </ActionGroup>
        <ActionButton
          variant="danger"
          disabled={!deleteEnabled}
          title={!deleteEnabled ? "Deallocate the SwiftIQ Agent before deleting it" : undefined}
          onClick={this.props.onDeleteMinionPoolClick}
        >
          <Trash2 size={16} />
          Delete SwiftIQ Agent
        </ActionButton>
      </DetailsActions>
    );
  }

  renderLoading() {
    return (
      <SectionCard testId="minionPoolDetails-loading">
        <Loading>
          <LoaderCircle size={20} />
          Loading the SwiftIQ Agent…
        </Loading>
      </SectionCard>
    );
  }

  renderMachines() {
    if (this.props.page !== "machines") {
      return null;
    }

    return (
      <MinionPoolMachines
        item={this.props.item}
        transfers={this.props.transfers}
        deployments={this.props.deployments}
      />
    );
  }

  renderEvents() {
    if (this.props.page !== "events") {
      return null;
    }

    return <MinionPoolEvents item={this.props.item} />;
  }

  renderMainDetails() {
    if (this.props.page !== "") {
      return null;
    }

    return (
      <MinionPoolMainDetails
        item={this.props.item}
        transfers={this.props.transfers}
        deployments={this.props.deployments}
        schema={this.props.schema}
        schemaLoading={this.props.schemaLoading}
        endpoints={this.props.endpoints}
        bottomControls={this.renderBottomControls()}
      />
    );
  }

  render() {
    const machines = this.props.item?.minion_machines;
    const tabs = NavigationItems.map(tab =>
      tab.value === "machines" && machines ? { ...tab, count: machines.length } : tab,
    );
    return (
      <DetailsPage>
        <DetailsTabs
          items={tabs}
          selectedValue={this.props.page}
          href={item =>
            `/minion-pools/${this.props.itemId}${item.value ? `/${item.value}` : ""}`
          }
        />
        {!this.props.loading ? this.renderMainDetails() : null}
        {!this.props.loading ? this.renderMachines() : null}
        {!this.props.loading ? this.renderEvents() : null}
        {this.props.loading ? this.renderLoading() : null}
      </DetailsPage>
    );
  }
}

export default MinionPoolDetailsContent;
